import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { urlResourceAPI, courseAPI } from "@/lib/api";
import { CourseGrouping } from "@/components/CourseGrouping";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Link2,
  Pencil,
  Trash2,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  Globe,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ICONS = [
  { value: "🔗", label: "Lien" },
  { value: "📄", label: "Document" },
  { value: "📹", label: "Vidéo" },
  { value: "🎓", label: "Formation" },
  { value: "📚", label: "Documentation" },
  { value: "🛠️", label: "Outil" },
  { value: "💻", label: "Code" },
  { value: "🌐", label: "Web" },
  { value: "📊", label: "Tableau de bord" },
  { value: "☁️", label: "Cloud" },
  { value: "🐳", label: "Docker" },
  { value: "⚙️", label: "Configuration" },
  { value: "📝", label: "Notes" },
  { value: "🧪", label: "Lab" },
  { value: "🎯", label: "Objectif" },
  { value: "📌", label: "Important" },
];

function getFaviconUrl(url) {
  try {
    const u = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=32`;
  } catch {
    return null;
  }
}

export default function UrlResourcesPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editResource, setEditResource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    url: "",
    icon: "🔗",
    course_id: "",
    is_visible: true,
    open_in_new_tab: true,
  });

  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadResources();
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadResources = async () => {
    try {
      const response = await urlResourceAPI.list(workspaceId);
      setResources(response.data);
    } catch (error) {
      console.error("Failed to load URL resources:", error);
      toast.error("Erreur lors du chargement des ressources URL");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      url: "",
      icon: "🔗",
      course_id: "",
      is_visible: true,
      open_in_new_tab: true,
    });
    setEditResource(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (resource) => {
    setEditResource(resource);
    setForm({
      title: resource.title,
      description: resource.description || "",
      url: resource.url,
      icon: resource.icon || "🔗",
      course_id: resource.course_id || "",
      is_visible: resource.is_visible !== false,
      open_in_new_tab: resource.open_in_new_tab !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.url) {
      toast.error("L'URL est obligatoire");
      return;
    }

    // Auto-detect title from URL if empty
    let title = form.title;
    if (!title) {
      try {
        const u = new URL(form.url);
        title = u.hostname.replace("www.", "");
      } catch {
        title = form.url;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        title,
        course_id: form.course_id || null,
      };

      if (editResource) {
        const response = await urlResourceAPI.update(workspaceId, editResource.id, payload);
        setResources(resources.map(r => r.id === editResource.id ? response.data : r));
        toast.success("Ressource URL mise à jour");
      } else {
        const response = await urlResourceAPI.create(workspaceId, payload);
        setResources([...resources, response.data]);
        toast.success("Ressource URL créée");
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to save URL resource:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await urlResourceAPI.delete(workspaceId, deleteId);
      setResources(resources.filter(r => r.id !== deleteId));
      toast.success("Ressource URL supprimée");
    } catch (error) {
      console.error("Failed to delete URL resource:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleToggleVisibility = async (resourceId) => {
    try {
      const response = await urlResourceAPI.toggleVisibility(workspaceId, resourceId);
      setResources(resources.map(r =>
        r.id === resourceId ? { ...r, is_visible: response.data.is_visible } : r
      ));
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Erreur lors de la modification de la visibilité");
    }
  };

  const filteredResources = search
    ? resources.filter(r =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.url.toLowerCase().includes(search.toLowerCase()) ||
        (r.description || "").toLowerCase().includes(search.toLowerCase())
      )
    : resources;

  const renderResourceCard = (resource, index, courseColor) => (
    <Card
      key={resource.id}
      className={cn(
        "card-hover transition-all group/card relative overflow-hidden pl-2",
        resource.is_visible === false && "opacity-60 border-dashed"
      )}
      style={courseColor ? { borderColor: `${courseColor}33` } : {}}
      data-testid={`url-resource-card-${index}`}
    >
      {courseColor && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-1.5 transition-all" 
          style={{ backgroundColor: courseColor }}
        />
      )}
      <CardContent className="flex items-center gap-4 p-4">
        {/* Icon / Favicon */}
        <div 
          className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 relative overflow-hidden transition-colors"
          style={courseColor ? { backgroundColor: `${courseColor}15` } : {}}
          {...(!courseColor ? { className: "flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20 shrink-0 relative overflow-hidden" } : {})}
        >
          {resource.icon ? (
            <span className="text-2xl">{resource.icon}</span>
          ) : (
            <Globe 
              className={cn("h-6 w-6 text-blue-600", courseColor && "text-foreground")} 
              style={courseColor ? { color: courseColor } : {}}
            />
          )}
          {/* Small favicon badge */}
          {getFaviconUrl(resource.url) && (
            <img
              src={getFaviconUrl(resource.url)}
              alt=""
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 bg-white"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <a
              href={resource.url}
              target={resource.open_in_new_tab ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="font-semibold hover:text-primary transition-colors flex items-center gap-1.5"
            >
              {resource.title}
              <ExternalLink className="h-3.5 w-3.5 opacity-50" />
            </a>
            {canManage && resource.is_visible === false && (
              <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                Caché
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
            {resource.description || resource.url}
          </p>
          <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1 truncate max-w-[200px]">
              <Link2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{(() => { try { return new URL(resource.url).hostname; } catch { return resource.url; } })()}</span>
            </span>
            {resource.created_by_name && (
              <span>par {resource.created_by_name}</span>
            )}
            <span>Créé le {formatDate(resource.created_at)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="icon" data-testid={`open-url-${index}`}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent>Ouvrir le lien</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {canManage && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleVisibility(resource.id)}
                      data-testid={`toggle-visibility-url-${index}`}
                    >
                      {resource.is_visible === false ? (
                        <EyeOff className="h-4 w-4 text-yellow-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {resource.is_visible === false
                      ? "Rendre visible aux candidats"
                      : "Cacher aux candidats"
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEditDialog(resource)}
                data-testid={`edit-url-${index}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteId(resource.id)}
                data-testid={`delete-url-${index}`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Liens URL</h1>
            <p className="text-muted-foreground mt-1">
              {resources.length} ressource{resources.length > 1 ? "s" : ""} URL dans cet espace
            </p>
          </div>
          {canManage && (
            <Button className="gap-2" onClick={openCreateDialog}>
              <Plus className="h-4 w-4" />
              Nouvelle URL
            </Button>
          )}
        </div>

        {/* Search */}
        {resources.length > 0 && (
          <div className="mb-6 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une ressource..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Empty State */}
        {resources.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                <Link2 className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold">Aucune ressource URL</h3>
              <p className="text-muted-foreground mt-2 text-center max-w-md">
                {canManage
                  ? "Ajoutez des liens vers des ressources externes : documentations, outils, vidéos, etc."
                  : "Aucune ressource URL n'est disponible pour le moment."}
              </p>
              {canManage && (
                <Button className="mt-4 gap-2" onClick={openCreateDialog}>
                  <Plus className="h-4 w-4" />
                  Ajouter une URL
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <CourseGrouping
            items={filteredResources}
            courses={courses}
            renderItem={renderResourceCard}
            emptyLabel="Sans cours"
            icon={Link2}
          />
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) { resetForm(); } setDialogOpen(v); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editResource ? "Modifier la ressource URL" : "Nouvelle ressource URL"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              {/* URL */}
              <div className="space-y-2">
                <Label htmlFor="url-input">URL *</Label>
                <Input
                  id="url-input"
                  type="url"
                  placeholder="https://example.com"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  required
                  data-testid="url-input"
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title-input">Titre</Label>
                <Input
                  id="title-input"
                  placeholder="Titre (auto-détecté si vide)"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="url-title-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="desc-input">Description</Label>
                <Textarea
                  id="desc-input"
                  placeholder="Description optionnelle..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  data-testid="url-description-input"
                />
              </div>

              {/* Icon picker */}
              <div className="space-y-2">
                <Label>Icône</Label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button
                      key={ic.value}
                      type="button"
                      onClick={() => setForm({ ...form, icon: ic.value })}
                      className={cn(
                        "h-9 w-9 rounded-lg flex items-center justify-center text-lg border transition-all",
                        form.icon === ic.value
                          ? "border-primary bg-primary/10 ring-2 ring-primary/30 scale-110"
                          : "border-border hover:border-primary/50 hover:bg-muted"
                      )}
                      title={ic.label}
                    >
                      {ic.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Course */}
              <div className="space-y-2">
                <Label>Cours associé</Label>
                <Select value={form.course_id || "__none__"} onValueChange={(v) => setForm({ ...form, course_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un cours" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Aucun cours</SelectItem>
                    {courses.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Options */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.open_in_new_tab}
                    onChange={(e) => setForm({ ...form, open_in_new_tab: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Ouvrir dans un nouvel onglet</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_visible}
                    onChange={(e) => setForm({ ...form, is_visible: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Visible aux candidats</span>
                </label>
              </div>

              {/* Preview */}
              {form.url && (
                <div className="rounded-lg border p-3 bg-muted/50">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Aperçu</p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{form.icon}</span>
                    {getFaviconUrl(form.url) && (
                      <img
                        src={getFaviconUrl(form.url)}
                        alt=""
                        className="w-4 h-4"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="font-medium text-sm">{form.title || (() => { try { return new URL(form.url).hostname; } catch { return form.url; } })()}</span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { resetForm(); setDialogOpen(false); }}>
                  Annuler
                </Button>
                <Button type="submit" disabled={saving} data-testid="url-save-btn">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editResource ? "Enregistrer" : "Créer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette ressource URL ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. La ressource sera définitivement supprimée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </WorkspaceLayout>
  );
}
